import { createFileRoute, Link } from "@tanstack/react-router";
import { PHYSICIAN_REF } from "@/lib/medical-schema";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { Flower } from "@/components/site/Flower";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import nutritionalSupplementGuidelines from "@/assets/Nutritional_Supplement_Guidelines.pdf.asset.json";
import approvedProteinSupplements from "@/assets/Approved_Protein_Supplements.pdf.asset.json";
import bypassPostoperativeDiet from "@/assets/Bypass_Postoperative_Diet.pdf.asset.json";
import sleevePostoperativeDiet from "@/assets/Sleeve_Postoperative_Diet.pdf.asset.json";
import { FileText } from "lucide-react";
import { schemaDateFields } from "@/lib/schema-dates";


export const Route = createFileRoute("/resources/nutrition")({
  head: () => ({
    meta: [
      { title: "Nutrition & Diet Guidelines | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Nutrition guidance and diet progression recommendations for Virginia Bariatric Surgery patients before and after weight-loss treatment.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Nutrition & Diet Guidelines | Virginia Bariatric" },
        { property: "og:description", content: "Nutrition guidance and diet progression recommendations for Virginia Bariatric Surgery patients before and after weight-loss treatment." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://virginiabariatric.com/resources/nutrition" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Nutrition & Diet Guidelines | Virginia Bariatric" },
        { name: "twitter:description", content: "Nutrition guidance and diet progression recommendations for Virginia Bariatric Surgery patients before and after weight-loss treatment." },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: "https://virginiabariatric.com/resources/nutrition" }],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          ...schemaDateFields("/resources/nutrition"),
          "@id": "https://virginiabariatric.com/resources/nutrition#webpage",
          url: "https://virginiabariatric.com/resources/nutrition",
          name: "Bariatric Nutrition & Diet Guidelines",
          description:
            "Nutrition, protein, and vitamin supplement guidelines and post-op diet progression for sleeve gastrectomy and gastric bypass patients.",
          inLanguage: "en-US",
          isPartOf: { "@id": "https://virginiabariatric.com/#website" },
          about: { "@type": "MedicalCondition", name: "Obesity" },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Post-bariatric surgery patients",
          },
          specialty: "Bariatric",
          reviewedBy: PHYSICIAN_REF,
        }),
      },
    ] : [],
  }),
  component: NutritionPage,
});

function NutritionPage() {
  return (
    <>
      <PageSection className="relative pt-4 md:pt-6 pb-3 md:pb-4">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div className="text-center max-w-4xl mx-auto">
          <EditableText
            as="h1"
            contentKey="nutrition.hero.title"
            defaultValue="Nutrition & Diet Guidelines"
            className="text-4xl md:text-5xl font-serif"
          />
          <EditableText
            as="p"
            contentKey="nutrition.hero.intro"
            defaultValue="Guidance on diet progression, protein goals, hydration, and long-term eating habits for weight-loss patients."
            className="mt-4 md:mt-6 text-foreground/80 leading-[1.6] text-base md:text-lg"
          />
        </div>
      </PageSection>

      <PageSection fullWidth maxWidth="4xl" className="relative z-10 bg-brand-soft py-3 md:py-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <a
            href={nutritionalSupplementGuidelines.url}
            download={nutritionalSupplementGuidelines.original_filename}
            type="application/pdf"
            className="group rounded-2xl border border-border bg-card py-3 px-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
          >
            <FileText className="h-6 w-6 text-brand-mid" />
            <h3 className="mt-2 text-lg text-brand-mid">Nutritional Supplement Guidelines</h3>
            <p className="mt-0.5 text-sm text-muted-foreground leading-[1.6]">
              Recommended vitamin and supplement regimen for bariatric patients.
            </p>
            <span className="mt-2 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
              Download PDF →
            </span>
          </a>
          <a
            href={approvedProteinSupplements.url}
            download={approvedProteinSupplements.original_filename}
            type="application/pdf"
            className="group rounded-2xl border border-border bg-card py-3 px-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
          >
            <FileText className="h-6 w-6 text-brand-mid" />
            <h3 className="mt-2 text-lg text-brand-mid">Approved Protein Supplements</h3>
            <p className="mt-0.5 text-sm text-muted-foreground leading-[1.6]">
              Protein supplement options approved for bariatric patients.
            </p>
            <span className="mt-2 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
              Download PDF →
            </span>
          </a>
          <a
            href={bypassPostoperativeDiet.url}
            download={bypassPostoperativeDiet.original_filename}
            type="application/pdf"
            className="group rounded-2xl border border-border bg-card py-3 px-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
          >
            <FileText className="h-6 w-6 text-brand-mid" />
            <h3 className="mt-2 text-lg text-brand-mid">Bypass Postoperative Diet</h3>
            <p className="mt-0.5 text-sm text-muted-foreground leading-[1.6]">
              Diet progression guidelines after Roux-en-Y gastric bypass.
            </p>
            <span className="mt-2 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
              Download PDF →
            </span>
          </a>
          <a
            href={sleevePostoperativeDiet.url}
            download={sleevePostoperativeDiet.original_filename}
            type="application/pdf"
            className="group rounded-2xl border border-border bg-card py-3 px-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
          >
            <FileText className="h-6 w-6 text-brand-mid" />
            <h3 className="mt-2 text-lg text-brand-mid">Sleeve Postoperative Diet</h3>
            <p className="mt-0.5 text-sm text-muted-foreground leading-[1.6]">
              Diet progression guidelines after sleeve gastrectomy.
            </p>
            <span className="mt-2 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
              Download PDF →
            </span>
          </a>
        </div>
      </PageSection>

      <PageSection className="pt-4 pb-8 text-center">

        <EditableText
          as="p"
          contentKey="nutrition.body"
          defaultValue="Detailed nutrition materials will be posted here. Contact our office for personalized nutrition guidance."
          className="text-foreground/80 leading-[1.6]"
        />
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Contact Us
          </Link>
          <Link
            to="/resources"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Back to Resources
          </Link>
        </div>
      </PageSection>
    </>
  );
}
